import React, { useState, useEffect } from 'react';
    import { motion } from 'framer-motion';
    import { useData } from '../context/DataContext';
    import SafeIcon from '../common/SafeIcon';
    import * as FiIcons from 'react-icons/fi';

    const { FiX, FiSave, FiPlus, FiTrash2 } = FiIcons;

    const StudentForm = ({ student, onClose, onSave }) => {
      const { grades, subjects, users, statuses, addStudent, updateStudent, parents, addParent } = useData();

      // Helper to initialize subjects
      const getInitialSubjects = () => {
        if (student?.subjects && Array.isArray(student.subjects) && student.subjects.length > 0) {
          return student.subjects.map(sub => ({ ...sub }));
        }
        if (student?.subject) {
          return [{ 
            subject: student.subject, 
            tutor: student.tutor || '', 
            feePerSession: student.feePerSession || '', 
            classSchedule: student.classSchedule || '' 
          }];
        }
        return [{ subject: '', tutor: '', feePerSession: '', classSchedule: '' }];
      };

      const [formData, setFormData] = useState({
        parentId: student?.parentId || '',
        studentName: student?.studentName || '',
        grade: student?.grade || '',
        status: student?.status || 'Active',
        classDuration: student?.classDuration || '60',
        customDuration: student?.customDuration || '',
        advancePayment: student?.advancePayment || '',
        lastPaid: student?.lastPaid || '',
        remarks: student?.remarks || '',
        subjects: getInitialSubjects()
      });

      const [showParentForm, setShowParentForm] = useState(false);
      const [newParent, setNewParent] = useState({ firstName: '', lastName: '', email: '', phone: '' });
      const [showCustomDuration, setShowCustomDuration] = useState(
        student?.classDuration && !['30', '45', '60', '90', '120'].includes(student.classDuration)
      );
      const [isSaving, setIsSaving] = useState(false);

      // Helper to get tutors filtered by subject
      const getTutorsForSubject = (subjectName) => {
        const activeTutors = users.filter(u => u.role === 'tutor' && u.status === 'active');
        
        const qualifiedTutors = activeTutors.filter(tutor => {
          // If tutor has specific subjects assigned, check if they teach this subject
          if (tutor.assignedSubjects && Array.isArray(tutor.assignedSubjects) && tutor.assignedSubjects.length > 0) {
            return tutor.assignedSubjects.includes(subjectName);
          }
          // If no subjects assigned, assume they are generalist or legacy
          return true;
        });

        // Always include System Administrator and format names
        const tutorNames = [
          'System Administrator',
          ...qualifiedTutors.map(u => u.name || `${u.firstName} ${u.lastName}`.trim())
        ];
        
        return [...new Set(tutorNames)]; // Remove duplicates
      };

      const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
          const incompleteSubjects = formData.subjects.filter(sub => 
            (sub.subject || sub.tutor || sub.feePerSession) && 
            (!sub.subject || !sub.tutor || (sub.feePerSession === '' || sub.feePerSession === null))
          );

          if (incompleteSubjects.length > 0) {
            alert('Please complete all required fields (Subject, Tutor, Fee) for all subject entries.');
            setIsSaving(false);
            return;
          }

          const validSubjects = formData.subjects.filter(sub => 
            sub.subject && sub.tutor && (sub.feePerSession !== '' && sub.feePerSession !== null)
          );

          if (validSubjects.length === 0) {
            alert('Please add at least one complete subject.');
            setIsSaving(false);
            return;
          }

          const finalDuration = showCustomDuration ? formData.customDuration : formData.classDuration;
          const totalFeePerSession = validSubjects.reduce((total, sub) => total + parseFloat(sub.feePerSession || 0), 0);

          const studentData = {
            ...formData,
            classDuration: finalDuration,
            subjects: validSubjects,
            feePerSession: totalFeePerSession.toString(),
            advanceBalance: student ? (student.advanceBalance || '0') : (formData.advancePayment || '0')
          };

          delete studentData.customDuration;
          delete studentData.advancePayment;

          if (student) {
            await updateStudent(student.id, studentData);
          } else {
            await addStudent(studentData);
          }
          onSave();
        } catch (error) {
          console.error("Error saving student:", error);
          alert(`Failed to save student: ${error.message}`);
        } finally {
          setIsSaving(false);
        }
      };

      const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
      };

      const handleSubjectChange = (index, field, value) => {
        const updatedSubjects = [...formData.subjects];
        updatedSubjects[index] = { ...updatedSubjects[index], [field]: value };
        
        // If subject changes, clear tutor if they don't teach the new subject?
        // Optional: keep it simple for now, user manually changes tutor if needed.
        // But we should refresh the tutor list UI which happens automatically on render.
        
        setFormData(prev => ({ ...prev, subjects: updatedSubjects }));
      };

      const addSubject = () => {
        setFormData(prev => ({
          ...prev,
          subjects: [...prev.subjects, { subject: '', tutor: '', feePerSession: '', classSchedule: '' }]
        }));
      };

      const removeSubject = (index) => {
        const updatedSubjects = formData.subjects.filter((_, i) => i !== index);
        if (updatedSubjects.length === 0) {
          updatedSubjects.push({ subject: '', tutor: '', feePerSession: '', classSchedule: '' });
        }
        setFormData(prev => ({ ...prev, subjects: updatedSubjects }));
      };

      const handleDurationChange = (value) => {
        if (value === 'custom') {
          setShowCustomDuration(true);
          setFormData(prev => ({ ...prev, customDuration: '' }));
        } else {
          setShowCustomDuration(false);
          setFormData(prev => ({ ...prev, classDuration: value }));
        }
      };

      const handleAddParent = async (e) => {
        e.preventDefault();
        if (newParent.firstName && newParent.lastName && newParent.email) {
          try {
            const parent = await addParent(newParent);
            setFormData(prev => ({ ...prev, parentId: parent.id }));
            setShowParentForm(false);
            setNewParent({ firstName: '', lastName: '', email: '', phone: '' });
          } catch (error) {
            console.error("Error adding parent:", error);
            alert("Failed to add parent.");
          }
        } else {
          alert("Please fill in at least First Name, Last Name and Email.");
        }
      };

      const selectedParent = parents.find(p => p.id === formData.parentId);

      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-xl shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-semibold text-gray-900">{student ? 'Edit Student' : 'Add New Student'}</h2>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><SafeIcon icon={FiX} className="text-xl" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              
              {/* Parent Section Omitted for Brevity - Keeping same as original */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Parent Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Select Parent *</label>
                    <div className="flex gap-2">
                      <select value={formData.parentId} onChange={(e) => handleChange('parentId', e.target.value)} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" required>
                        <option value="">Select a parent</option>
                        {parents.map(parent => (
                          <option key={parent.id} value={parent.id}>{parent.firstName} {parent.lastName} - {parent.email}</option>
                        ))}
                      </select>
                      <button type="button" onClick={() => setShowParentForm(true)} className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"><SafeIcon icon={FiPlus} /></button>
                    </div>
                  </div>
                  {selectedParent && (
                    <div className="bg-white p-3 rounded border">
                      <h4 className="font-medium text-gray-900">{selectedParent.firstName} {selectedParent.lastName}</h4>
                      <p className="text-sm text-gray-600">{selectedParent.email}</p>
                    </div>
                  )}
                </div>
                {/* Parent Quick Add Form */}
                {showParentForm && (
                  <div className="mt-4 p-4 border border-green-200 rounded-lg bg-green-50">
                    <h4 className="font-medium text-green-900 mb-3">Add New Parent</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <input type="text" placeholder="First Name *" value={newParent.firstName} onChange={(e) => setNewParent(prev => ({ ...prev, firstName: e.target.value }))} className="px-3 py-2 border border-green-300 rounded-lg" />
                      <input type="text" placeholder="Last Name *" value={newParent.lastName} onChange={(e) => setNewParent(prev => ({ ...prev, lastName: e.target.value }))} className="px-3 py-2 border border-green-300 rounded-lg" />
                      <input type="email" placeholder="Email *" value={newParent.email} onChange={(e) => setNewParent(prev => ({ ...prev, email: e.target.value }))} className="px-3 py-2 border border-green-300 rounded-lg" />
                      <input type="tel" placeholder="Phone" value={newParent.phone} onChange={(e) => setNewParent(prev => ({ ...prev, phone: e.target.value }))} className="px-3 py-2 border border-green-300 rounded-lg" />
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button type="button" onClick={handleAddParent} className="px-4 py-2 bg-green-600 text-white rounded-lg">Add Parent</button>
                      <button type="button" onClick={() => setShowParentForm(false)} className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg">Cancel</button>
                    </div>
                  </div>
                )}
              </div>

              {/* Student Info */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Student Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Student Name *</label>
                    <input type="text" value={formData.studentName} onChange={(e) => handleChange('studentName', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Grade *</label>
                    <select value={formData.grade} onChange={(e) => handleChange('grade', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" required>
                      <option value="">Select Grade</option>
                      {grades.map(grade => <option key={grade} value={grade}>{grade}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status *</label>
                    <select value={formData.status} onChange={(e) => handleChange('status', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" required>
                      {statuses.map(status => <option key={status} value={status}>{status}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Subjects & Tutors */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Subjects & Tutors</h3>
                  <button type="button" onClick={addSubject} className="flex items-center gap-2 px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                    <SafeIcon icon={FiPlus} /> Add Subject
                  </button>
                </div>
                <div className="space-y-4">
                  {formData.subjects.map((subjectData, index) => {
                    // Filter tutors based on selected subject
                    const availableTutors = getTutorsForSubject(subjectData.subject);
                    
                    return (
                      <div key={index} className="bg-gray-50 p-4 rounded-lg border relative">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium text-gray-900">Subject {index + 1}</h4>
                          <button type="button" onClick={() => removeSubject(index)} className="text-red-600 hover:text-red-800 p-1">
                            <SafeIcon icon={FiTrash2} />
                          </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Subject *</label>
                            <select value={subjectData.subject} onChange={(e) => handleSubjectChange(index, 'subject', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" required>
                              <option value="">Select Subject</option>
                              {subjects.map(subject => <option key={subject} value={subject}>{subject}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Tutor *</label>
                            <select 
                              value={subjectData.tutor} 
                              onChange={(e) => handleSubjectChange(index, 'tutor', e.target.value)} 
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" 
                              required
                              disabled={!subjectData.subject}
                            >
                              <option value="">Select Tutor</option>
                              {availableTutors.map(tutor => (
                                <option key={tutor} value={tutor}>{tutor}</option>
                              ))}
                            </select>
                            {!subjectData.subject && <span className="text-xs text-gray-500">Select subject first</span>}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Fee Per Session *</label>
                            <input type="number" value={subjectData.feePerSession} onChange={(e) => handleSubjectChange(index, 'feePerSession', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="0.00" step="0.01" required />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Class Schedule</label>
                            <input type="text" value={subjectData.classSchedule} onChange={(e) => handleSubjectChange(index, 'classSchedule', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="e.g., Mon/Wed 4PM" />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200 mt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-indigo-900">Total Fee Per Session:</span>
                    <span className="text-lg font-bold text-indigo-900">${formData.subjects.reduce((total, sub) => total + parseFloat(sub.feePerSession || 0), 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Class Settings */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Class Settings</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Class Duration (minutes) *</label>
                    <select value={showCustomDuration ? 'custom' : formData.classDuration} onChange={(e) => handleDurationChange(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" required>
                      <option value="30">30 minutes</option>
                      <option value="45">45 minutes</option>
                      <option value="60">60 minutes</option>
                      <option value="90">90 minutes</option>
                      <option value="120">120 minutes</option>
                      <option value="custom">Custom Duration</option>
                    </select>
                    {showCustomDuration && (
                      <div className="mt-2">
                        <input type="number" value={formData.customDuration} onChange={(e) => handleChange('customDuration', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="Enter custom duration in minutes" min="1" max="300" required />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Financial Info */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Financial Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Advance Payment</label>
                    <input type="number" value={formData.advancePayment} onChange={(e) => handleChange('advancePayment', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="0.00" step="0.01" disabled={!!student} />
                    <p className="text-xs text-gray-500 mt-1">{student ? 'To add funds, use the Payment button in the student list.' : 'This will be set as initial balance'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Last Paid</label>
                    <input type="date" value={formData.lastPaid} onChange={(e) => handleChange('lastPaid', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Remarks</label>
                <textarea value={formData.remarks} onChange={(e) => handleChange('remarks', e.target.value)} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="Any additional notes..." />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button type="button" onClick={onClose} className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50" disabled={isSaving}>Cancel</button>
                <motion.button whileHover={{ scale: isSaving ? 1 : 1.05 }} whileTap={{ scale: isSaving ? 1 : 0.95 }} type="submit" disabled={isSaving} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                  {isSaving ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Saving...</>) : (<><SafeIcon icon={FiSave} /> {student ? 'Update' : 'Save'} Student</>)}
                </motion.button>
              </div>
            </form>
          </motion.div>
        </div>
      );
    };

    export default StudentForm;